import { cabecalhosDoApp } from "@/lib/cabecalhos-do-app"
import authService from "./auth-service"
import { uploadFile } from "../upload/upload-file"

// === Page Builder Types ===

export type PageBlockType =
  | "hero"
  | "texto"
  | "formulario"
  | "servicos"
  | "galeria"
  | "cta"
  | "depoimentos"
  | "redes_sociais"
  | "espacamento"
  | "checkout"
  | "badge"
  | "beneficios"
  | "imagem"
  | "video"
  | "faq"
  | "destaque"
  | "divisor"
  | "garantia"
  | "html"
  | "quiz"

export interface PageBlock {
  id: string
  tipo: PageBlockType
  props: Record<string, any>
  ordem: number
}

export type PageThemePreset = "white" | "lime" | "purple" | "ocean" | "warm" | "dark"

export interface PageTheme {
  preset: PageThemePreset
  background: string
  foreground: string
  muted: string
  primary: string
  primary_fg: string
  surface: string
  border: string
  accent: string
  font_heading: "display" | "body" | "serif"
  font_body: "body" | "display" | "serif"
  radius: "none" | "sm" | "md" | "lg" | "full"
  container_max: "sm" | "md" | "lg"
}

// === Lead Types ===

export interface Lead {
  id: string
  nome: string
  email: string
  telefone: string
  origem: string
  created_at: string
}

export interface LeadListResponse {
  dados: Lead[]
  total: number
  pagina: number
  limite: number
  totalPaginas: number
}

export interface OrigemCount {
  origem: string
  count: number
}

export interface AcessoDia {
  date: string
  count: number
}

export interface LeadMetricas {
  leads_ultimo_mes: number
  acessos_ultimo_mes: number
  acessos_por_dia: AcessoDia[]
  leads_por_origem: OrigemCount[]
  acessos_por_origem: OrigemCount[]
}

export interface PersonalPublico {
  nome: string
  sobrenome: string
  apelido: string
  foto_url: string | null
  verificado?: boolean
}

class LeadService {
  private baseUrl = process.env.NEXT_PUBLIC_API_BASE_URL || "https://api.welltrainer.com.br"

  // --- Endpoints publicos ---

  async buscarPersonal(apelido: string): Promise<PersonalPublico> {
    const res = await fetch(`${this.baseUrl}/lead/personal/${encodeURIComponent(apelido)}`)

    if (!res.ok) {
      const data = await res.json().catch(() => ({}))
      throw new Error(data.mensagem || "Personal não encontrado.")
    }

    return res.json()
  }

  async registrarLead(dados: {
    personal_apelido: string
    nome: string
    email: string
    telefone: string
    origem?: string
  }): Promise<{ id: string }> {
    const res = await fetch(`${this.baseUrl}/lead/registrar`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(dados),
    })

    if (!res.ok) {
      const data = await res.json().catch(() => ({}))
      /* A recusa da validação vem em `message` (lista), e não em `mensagem`: sem ler
         as duas, o visitante via "Erro ao registrar lead." no lugar do motivo. */
      const motivo = data.mensagem || (Array.isArray(data.message) ? data.message.join(" ") : data.message)
      throw new Error(motivo || "Não deu para enviar agora. Tente de novo em instantes.")
    }

    return res.json()
  }

  async registrarAcesso(dados: { personal_apelido: string; origem?: string }): Promise<void> {
    await fetch(`${this.baseUrl}/lead/acesso`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(dados),
    }).catch(() => {})
  }

  // --- Endpoints de pagina ---

  async buscarPagina(
    token: string,
  ): Promise<{ blocos: PageBlock[]; tema: PageTheme | null; publicada: boolean }> {
    const res = await fetch(`${this.baseUrl}/lead/pagina`, {
      headers: { ...cabecalhosDoApp(), Authorization: token },
    })

    if (!res.ok) {
      const data = await res.json().catch(() => ({}))
      throw new Error(data.mensagem || "Erro ao carregar página.")
    }

    return res.json()
  }

  async salvarPagina(
    token: string,
    blocos: PageBlock[],
    publicada: boolean,
    tema?: PageTheme,
  ): Promise<void> {
    const res = await fetch(`${this.baseUrl}/lead/pagina`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        ...cabecalhosDoApp(),
        Authorization: token,
      },
      body: JSON.stringify({ blocos, publicada, tema }),
    })

    if (!res.ok) {
      const data = await res.json().catch(() => ({}))
      throw new Error(data.mensagem || "Erro ao salvar página.")
    }
  }

  /** Os blocos e o TEMA: sem o tema, a página no ar ignorava a escolha de cor. */
  async buscarPaginaPublica(
    apelido: string,
  ): Promise<{ blocos: PageBlock[]; tema?: PageTheme | string | null }> {
    const res = await fetch(`${this.baseUrl}/lead/personal/${encodeURIComponent(apelido)}/pagina`)

    if (!res.ok) {
      return { blocos: [] }
    }

    return res.json()
  }

  // --- Endpoints autenticados ---

  async listarLeads(token: string, pagina = 1, limite = 10): Promise<LeadListResponse> {
    const res = await fetch(`${this.baseUrl}/lead?pagina=${pagina}&limite=${limite}`, {
      headers: { ...cabecalhosDoApp(), Authorization: token },
    })

    if (!res.ok) {
      const data = await res.json().catch(() => ({}))
      throw new Error(data.mensagem || "Erro ao carregar leads.")
    }

    return res.json()
  }

  async buscarMetricas(token: string): Promise<LeadMetricas> {
    const res = await fetch(`${this.baseUrl}/lead/metricas`, {
      headers: { ...cabecalhosDoApp(), Authorization: token },
    })

    if (!res.ok) {
      const data = await res.json().catch(() => ({}))
      throw new Error(data.mensagem || "Erro ao carregar métricas.")
    }

    return res.json()
  }

  async uploadImagem(file: File): Promise<{ url: string }> {
    const formData = new FormData()
    formData.append("imagem", file)
    return uploadFile<{ url: string }>({
      path: "/lead/upload-imagem",
      formData,
      label: file.name || "Imagem",
    })
  }

  /** Vídeo do próprio personal para o bloco de vídeo da página. Teto de 500MB. */
  async uploadVideo(file: File): Promise<{ url: string }> {
    const formData = new FormData()
    formData.append("video", file)
    return uploadFile<{ url: string }>({
      path: "/lead/upload-video",
      formData,
      label: file.name || "Vídeo",
    })
  }
}

export default new LeadService()
